import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, DatePicker, Button, Space, Tag } from 'antd';
import { CalendarOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTimesheetStore } from '../../store/useTimesheetStore';
import { useAuthStore } from '../../store/useAuthStore';
import ErrorDisplay from '../common/ErrorDisplay';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const TimesheetHistory = () => {
  const { user } = useAuthStore();
  const { timesheets, loading, error, fetchTimesheets, clearError } = useTimesheetStore();
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);  
  useEffect(() => {
    if (user?.id) {
      const startDate = dayjs().startOf('month').format('YYYY-MM-DD');
      const endDate = dayjs().endOf('month').format('YYYY-MM-DD');
      fetchTimesheets(user.id, startDate, endDate);
    }
  }, [user?.id, fetchTimesheets]);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format('MMM DD, YYYY'),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Project',
      dataIndex: 'projectName',
      key: 'projectName',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      render: (hours) => `${hours}h`,
      sorter: (a, b) => a.hours - b.hours,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'approved' ? 'green' : status === 'pending' ? 'orange' : 'red'}>
          {status?.toUpperCase() || 'SUBMITTED'}
        </Tag>
      ),
    },
  ];

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && user?.id) {
      const startDate = dates[0].format('YYYY-MM-DD');
      const endDate = dates[1].format('YYYY-MM-DD');
      fetchTimesheets(user.id, startDate, endDate);
    }
  };

  const arr = Array.isArray(timesheets) ? timesheets : timesheets?.data || [];
  const totalHours = arr.reduce((sum, t) => sum + Number(t.hours || 0), 0);
  return (
    <div className="p-4 sm:p-6">
      <Card className="shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <Title level={3} className="mb-4 sm:mb-0">
            <CalendarOutlined className="mr-2" />
            Timesheet History
          </Title>
          
          <Space direction="vertical" size="middle" className="w-full sm:w-auto">
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              className="w-full sm:w-auto"
              aria-label="Select date range for timesheet history"
            />
            <div className="text-right">
              <span className="text-sm text-gray-600">
                Total Hours: <strong>{totalHours}h</strong>
              </span>
            </div>
          </Space>
        </div>

        {error && (
          <div className="mb-4">
            <ErrorDisplay
              errorCode="FETCH_ERROR"
              errorMessage={error}
              closable
              onClose={clearError}
            />
          </div>
        )}

        <Table
          columns={columns}
          dataSource={Array.isArray(timesheets?.data) ? timesheets?.data : []}
          loading={loading}
          rowKey="_id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} entries`,
          }}
          scroll={{ x: 800 }}
          className="responsive-table"
        />

        <div className="mt-4 flex justify-end">
          {/* <Button
            type="default"
            icon={<DownloadOutlined />}
            onClick={() => {
             handleExport();
            }}
            aria-label="Export timesheet data"
          >
            Export
          </Button> */}
        </div>
      </Card>
    </div>
  );
};

export default TimesheetHistory;
